import DiscoveryScheduling from "./DiscoveryScheduling";
import useProfileTab from "../hooks/useProfileTab";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IosShareOutlined as IosShareOutlinedIcon } from "./icons/FigmaControlIcons";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Alert, Box, LinearProgress, Typography } from "@mui/material";

import ProfileHeader from "./TabsHeader";
import StatsSlider from "./StatsSlider";
import ListTableTabsTemplate from "./layouts/ListTableTabsTemplate";
import QuickActionsCard from "./sidebar-widgets/QuickActionsCard";
import AIAgentCard from "./sidebar-widgets/AIAgentCard";
import HiringTeamCard from "./sidebar-widgets/HiringTeamCard";
import JobDetailsContent from "./job-details/JobDetailsContent";
import ShareJobDialog from "./ShareJobDialog";
import AddToSequenceDialog from "./PopupWindows/AddToSequenceDialog";
import { buildLeadSequenceItem } from "../utils/sequenceItems";
import PitchPackageDialog from "./PopupWindows/PitchPackageDialog";
import { TAB_VIEW_CONTENT_WRAPPER_SX, TAB_VIEW_HEADER_SX } from "./tabViewContentSpacing";
import { MatchActionIcon, SequenceActionIcon } from "./ActionButtonIcons";
import AssignTeamPillIcon from "./icons/AssignTeamPillIcon";
import TeamMemberAddIcon from "./icons/TeamMemberAddIcon";
import { formatEmploymentType } from "../utils/jobEmploymentTypes";
import { resolveEntityAddedDate } from "../utils/entityDateText";
import { formatLocalDate } from "../utils/dateTime";
import { normalizeLocationFilterValues } from "../utils/locationFilters";
import { LEAD_STATUS_OPTIONS } from "../utils/leadStatusOptions";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BORDER = "#E5E7EB";
const LEAD_STATUS_ACTION_OPTIONS = LEAD_STATUS_OPTIONS;
const LEAD_STATUS_LABEL_BY_KEY = new Map([
  ["new", "New"],
  ["contacted", "Contacted"],
  ["discovery", "Discovery"],
  ["proposal", "Proposal"],
  ["client", "Client"],
  ["active", "Client"],
  ["lost", "Lost"],
  ["do not contact", "Do not contact"],
  ["no stage", "New"],
]);

const normalizeLeadStatusLabel = (
  value,
  { fallback = "New", preserveUnknown = false } = {}
) => {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;

  const normalized = LEAD_STATUS_LABEL_BY_KEY.get(raw.toLowerCase());
  if (normalized) return normalized;

  return preserveUnknown ? raw : fallback;
};

const normalizeTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "stages", "matches", "activities"];
  return validTabs.includes(normalized) ? normalized : "details";
};

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  localStorage.getItem("accessToken") ||
  "";

const getWorkflowMetadata = (activity = {}) => {
  const metadata = activity?.metadata;
  if (metadata && typeof metadata === "object") return metadata;
  if (typeof metadata !== "string") return {};
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getActiveLeadEnrichment = (activities = []) =>
  (Array.isArray(activities) ? activities : []).find((activity) => {
    const metadata = getWorkflowMetadata(activity);
    const workflowKind = String(metadata.workflow_kind || "").toLowerCase();
    const status = String(metadata.workflow_status || metadata.status || "").toLowerCase();
    const progress = Number(metadata.progress ?? metadata.percent_complete ?? 0);
    return (
      workflowKind === "lead_link_enrichment" &&
      ["queued", "running", "pending", "processing", "in_progress"].includes(status) &&
      (!Number.isFinite(progress) || progress < 100)
    );
  }) || null;

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    } catch {}

    return trimmed
      .split(/\s*(?:,|\|)\s*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeDraftTextValue = (value) => {
  const nextValue = String(value ?? "").trim();
  return nextValue === "-" ? "" : nextValue;
};

const uniqTextValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

const toFiniteCount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const countDistinctStatsValues = (values = []) =>
  new Set(
    (Array.isArray(values) ? values : [])
      .map((item) => String(item || "").trim())
      .filter((item) => item && item !== "-")
  ).size;

const normalizeComparableTextBlock = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\W_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveDistinctResponsibilitiesText = (responsibilitiesValue, descriptionValue) => {
  const responsibilities = String(responsibilitiesValue || "").trim();
  if (!responsibilities) return "";

  const description = String(descriptionValue || "").trim();
  if (!description) return responsibilities;

  return normalizeComparableTextBlock(responsibilities) ===
    normalizeComparableTextBlock(description)
    ? ""
    : responsibilities;
};

const formatFileSize = (size) => {
  const numericValue = Number(size);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "";
  if (numericValue < 1024) return `${numericValue} B`;
  if (numericValue < 1024 * 1024) return `${Math.round(numericValue / 1024)} KB`;
  return `${(numericValue / (1024 * 1024)).toFixed(1)} MB`;
};

const toDomain = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./, "");
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }
};

const normalizeDocuments = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((doc) => {
      if (!doc || typeof doc !== "object") return null;

      const name =
        String(doc.name || doc.original_name || doc.filename || "Document.pdf").trim() ||
        "Document.pdf";
      const metaParts = [];

      const sizeLabel = formatFileSize(doc.size);
      if (sizeLabel) metaParts.push(sizeLabel);

      if (String(doc.mime_type || "").toLowerCase().includes("pdf") || /\.pdf$/i.test(name)) {
        metaParts.push("PDF");
      }

      if (doc.addedAt) {
        const addedLabel = formatLocalDate(doc.addedAt, {}, "");
        if (addedLabel) metaParts.push(addedLabel);
      }

      return {
        ...doc,
        name,
        url: String(doc.url || "").trim(),
        meta: metaParts.join(" - ") || "PDF",
      };
    })
    .filter(Boolean);
};

const getLeadDocuments = (lead = {}) => {
  if (Array.isArray(lead?.documents)) return lead.documents;
  if (Array.isArray(lead?.documents_json)) return lead.documents_json;
  if (Array.isArray(lead?.documentsJson)) return lead.documentsJson;
  return [];
};

const getLeadLinkedJobId = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    const candidates = [
      source?.job_id,
      source?.jobId,
      source?.related_job_id,
      source?.relatedJobId,
      source?.matched_job_id,
      source?.matchedJobId,
      source?.job?.id,
      source?.related_job?.id,
    ];

    for (const candidate of candidates) {
      const jobId = Number(candidate || 0);
      if (Number.isFinite(jobId) && jobId > 0) {
        return jobId;
      }
    }
  }

  return 0;
};

const resolveLeadRecordId = (source) => {
  if (!source || typeof source !== "object") return 0;

  const candidates = [
    source?.lead_id,
    source?.leadId,
    source?.id,
    source?.entity_id,
  ];

  for (const candidate of candidates) {
    const leadId = Number(candidate || 0);
    if (Number.isFinite(leadId) && leadId > 0) {
      return leadId;
    }
  }

  return 0;
};

const mergeLeadDetailState = (previousLead, nextLead) => {
  if (!nextLead) return previousLead || null;

  const previous = previousLead && typeof previousLead === "object" ? previousLead : {};
  const next = nextLead && typeof nextLead === "object" ? nextLead : {};
  const previousLeadId = resolveLeadRecordId(previous);
  const nextLeadId = resolveLeadRecordId(next);

  const hasNextContacts = Array.isArray(next.contacts);
  const hasNextJobs = Array.isArray(next.jobs);
  const hasNextActivities = Array.isArray(next.activities);
  const hasNextDocuments =
    Array.isArray(next.documents) ||
    Array.isArray(next.documents_json) ||
    Array.isArray(next.documentsJson);

  const documents = hasNextDocuments ? getLeadDocuments(next) : getLeadDocuments(previous);

  if (previousLeadId && nextLeadId && previousLeadId !== nextLeadId) {
    return {
      ...next,
      id: nextLeadId,
      contacts: hasNextContacts ? next.contacts : [],
      jobs: hasNextJobs ? next.jobs : [],
      activities: hasNextActivities ? next.activities : [],
      documents_json: documents,
      documentsJson: documents,
    };
  }

  return {
    ...previous,
    ...next,
    id: nextLeadId || previousLeadId || next?.id || previous?.id || null,
    contacts: hasNextContacts ? next.contacts : Array.isArray(previous.contacts) ? previous.contacts : [],
    jobs: hasNextJobs ? next.jobs : Array.isArray(previous.jobs) ? previous.jobs : [],
    activities: hasNextActivities
      ? next.activities
      : Array.isArray(previous.activities)
      ? previous.activities
      : [],
    documents_json: documents,
    documentsJson: documents,
  };
};

export default function LeadDetailsView({
  lead,
  initialTab = "details",
  detailFieldOptions,
  onTabChange,
  onResolvedTitleChange,
  onLeadChange,
  user,
}) {
  const navigate = useNavigate();
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [discoveryActionTarget, setDiscoveryActionTarget] = useState(null);
  const [leadLive, setLeadLive] = useState(lead || null);
  const detailRequestRef = useRef(0);
  const currentLeadIdRef = useRef(resolveLeadRecordId(lead));
  currentLeadIdRef.current = resolveLeadRecordId(lead);
  const [linkedJobLive, setLinkedJobLive] = useState(null);
  const [status, setStatus] = useState(() =>
    normalizeLeadStatusLabel(lead?.status, {
      fallback: "New",
      preserveUnknown: true,
    })
  );
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [tab, setTab] = useProfileTab(initialTab, normalizeTab, onTabChange);
  const [matchingCandidates, setMatchingCandidates] = useState(false);
  const [matchesRefreshKey, setMatchesRefreshKey] = useState(0);
  const [prefetchedMatchRows, setPrefetchedMatchRows] = useState(null);
  const [leadMatchRows, setLeadMatchRows] = useState([]);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [pitchPackageDialogOpen, setPitchPackageDialogOpen] = useState(false);

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getToken();
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(token ? { "x-access-token": token } : {}),
    };
    return fetch(url, { ...options, headers, credentials: "include" });
  }, []);
  const linkedJobId = useMemo(() => getLeadLinkedJobId(leadLive, lead), [lead, leadLive]);
  const resolvedLeadId = resolveLeadRecordId(lead) || resolveLeadRecordId(leadLive);
  const canPersistLead = Number(resolvedLeadId || 0) > 0;
  const resolvedLeadTitle = useMemo(() => {
    const activeLead = mergeLeadDetailState(lead, leadLive) || {};
    const linkedJob = linkedJobLive && typeof linkedJobLive === "object" ? linkedJobLive : {};

    return (
      activeLead?.job_title ||
      activeLead?.jobTitle ||
      linkedJob?.job_title ||
      linkedJob?.jobTitle ||
      linkedJob?.title ||
      activeLead?.title ||
      activeLead?.company_name ||
      activeLead?.name ||
      "Lead"
    );
  }, [lead, leadLive, linkedJobLive]);

  useEffect(() => {
    setLeadLive((previous) => {
      const previousId = resolveLeadRecordId(previous);
      const nextId = resolveLeadRecordId(lead);
      // Keep the live detail record authoritative while viewing one lead.
      // Parent list renders pass fresh (but older) object references.
      if (previousId && nextId && previousId === nextId) return previous;
      return mergeLeadDetailState(previous, lead);
    });
  // The prop object is intentionally excluded to prevent a stale table row
  // from replacing a just-refreshed lead activity list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.lead_id]);

  useEffect(() => {
    if (typeof onResolvedTitleChange !== "function") return;
    onResolvedTitleChange(String(resolvedLeadTitle || "").trim());
  }, [onResolvedTitleChange, resolvedLeadTitle]);

  useEffect(() => {
    setStatus(
      // The parent list can still hold the previous status after this view
      // saves an update. Prefer the live detail record so that stale props do
      // not immediately overwrite the new status shown in the header.
      normalizeLeadStatusLabel(leadLive?.status || lead?.status, {
        fallback: "New",
        preserveUnknown: true,
      })
    );
  }, [lead?.status, leadLive?.status]);

  useEffect(() => {
    if (!linkedJobId) {
      setLinkedJobLive(null);
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    setLinkedJobLive(null);

    authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(linkedJobId)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(text || `Failed to fetch matched job details (${response.status})`);
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        setLinkedJobLive(payload?.job || null);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load linked job details for lead:", error);
        setLinkedJobLive(null);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [authFetch, linkedJobId]);

  useEffect(() => {
    setPrefetchedMatchRows(null);
    setLeadMatchRows([]);
  }, [resolvedLeadId]);

  useEffect(() => {
    if (tab !== "stages" || !resolvedLeadId) return undefined;

    let active = true;
    const controller = new AbortController();
    authFetch(
      `${API_BASE}/api/leads/${encodeURIComponent(resolvedLeadId)}/candidates?staged=1`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to load staged candidates (${response.status})`);
        return response.json();
      })
      .then((payload) => {
        if (active) setLeadMatchRows(Array.isArray(payload?.rows) ? payload.rows : []);
      })
      .catch((error) => {
        if (active && error?.name !== "AbortError") {
          console.error("Failed to load staged lead candidates:", error);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [authFetch, resolvedLeadId, tab]);

  const refreshLeadDetails = useCallback(async () => {
    if (!resolvedLeadId) return null;
    const requestId = ++detailRequestRef.current;

    const baseResponse = await authFetch(`${API_BASE}/api/leads/${encodeURIComponent(resolvedLeadId)}`);
    if (!baseResponse.ok) {
      const text = await baseResponse.text().catch(() => "");
      throw new Error(text || `Failed to fetch lead details (${baseResponse.status})`);
    }

    const basePayload = await baseResponse.json().catch(() => ({}));
    const baseLead = basePayload?.lead || basePayload || {};

    let infoLead = {};
    try {
      const infoResponse = await authFetch(`${API_BASE}/api/leads/${encodeURIComponent(resolvedLeadId)}/info`);
      if (infoResponse.ok) {
        infoLead = await infoResponse.json().catch(() => ({}));
      }
    } catch (error) {
      console.error("Failed to load lead info payload:", error);
    }

    const mergedLead = mergeLeadDetailState(baseLead, infoLead);
    if (requestId !== detailRequestRef.current ||
        (currentLeadIdRef.current && currentLeadIdRef.current !== resolvedLeadId)) return null;
    setLeadLive((previous) => mergeLeadDetailState(previous, mergedLead));
    return mergedLead;
  }, [authFetch, resolvedLeadId]);

  useEffect(() => {
    if (!resolvedLeadId) return;

    refreshLeadDetails().catch((error) => {
      console.error("Failed to load lead details view data:", error);
    });
    return () => { detailRequestRef.current += 1; };
  }, [refreshLeadDetails, resolvedLeadId]);

  useEffect(() => {
    const refresh = () => refreshLeadDetails().catch(() => {});
    window.addEventListener("rpo:activities-refresh", refresh);
    return () => window.removeEventListener("rpo:activities-refresh", refresh);
  }, [refreshLeadDetails]);

  const activeLeadEnrichment = useMemo(
    () => getActiveLeadEnrichment(leadLive?.activities),
    [leadLive?.activities]
  );
  const leadEnrichmentProgress = Math.min(
    100,
    Math.max(0, Number(getWorkflowMetadata(activeLeadEnrichment).progress || 0))
  );

  // The activity record is persisted by the API. Poll only while that record
  // is active so the notice survives navigation/reload and disappears as soon
  // as the background agent completes its work.
  useEffect(() => {
    if (!activeLeadEnrichment) return undefined;
    const interval = window.setInterval(() => {
      refreshLeadDetails().catch(() => {});
    }, 2500);
    return () => window.clearInterval(interval);
  }, [activeLeadEnrichment, refreshLeadDetails]);

  const handleAddDocument = useCallback(
    async (file) => {
      const leadId = resolvedLeadId;
      if (!leadId || !file) return;

      const isPdf =
        String(file.type || "").toLowerCase() === "application/pdf" ||
        /\.pdf$/i.test(String(file.name || ""));

      if (!isPdf) {
        setDocumentsError("Only PDF files are supported.");
        return;
      }

      try {
        setDocumentsUploading(true);
        setDocumentsError("");

        const formData = new FormData();
        formData.append("document", file);

        const response = await authFetch(
          `${API_BASE}/api/leads/${encodeURIComponent(leadId)}/documents`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(text || `Failed to upload document (${response.status})`);
        }

        await refreshLeadDetails();
      } catch (error) {
        console.error("Failed to upload lead document:", error);
        setDocumentsError(error?.message || "Failed to upload lead document.");
      } finally {
        setDocumentsUploading(false);
      }
    },
    [authFetch, refreshLeadDetails, resolvedLeadId]
  );

  const handleSaveDetails = useCallback(
    async (draft = {}) => {
      const leadId = resolvedLeadId;
      if (!leadId) {
        throw new Error("Missing lead id.");
      }

      const payload = {
        contact_name: normalizeDraftTextValue(draft.primaryContact),
        contact_title: normalizeDraftTextValue(draft.titleField),
        contact_role: normalizeDraftTextValue(draft.titleField),
        salary_range: normalizeDraftTextValue(draft.salaryRange),
        compensation: normalizeDraftTextValue(draft.salaryRange),
        location: normalizeDraftTextValue(draft.location),
        locations: Array.isArray(draft.locationValues) ? draft.locationValues : [],
        industries: Array.isArray(draft.industryValues) ? draft.industryValues : [],
        industry_sectors_json: Array.isArray(draft.industryValues) ? draft.industryValues : [],
        employment_type: normalizeDraftTextValue(draft.employmentType),
        company_size: normalizeDraftTextValue(draft.companySize),
        industry_sector: normalizeDraftTextValue(draft.industry),
        company_website: normalizeDraftTextValue(draft.companyWebsite),
        career_site_url: normalizeDraftTextValue(draft.careerWebsite),
        career_website: normalizeDraftTextValue(draft.careerWebsite),
        company_career_website: normalizeDraftTextValue(draft.careerWebsite),
        source: normalizeDraftTextValue(draft.source),
        lead_link: normalizeDraftTextValue(draft.leadLink),
        other_link: normalizeDraftTextValue(draft.otherLink),
        linkedin_url: normalizeDraftTextValue(draft.linkedinUrl),
        facebook_url: normalizeDraftTextValue(draft.facebookUrl),
        twitter_url: normalizeDraftTextValue(draft.twitterUrl),
        instagram_url: normalizeDraftTextValue(draft.instagramUrl),
        youtube_url: normalizeDraftTextValue(draft.youtubeUrl),
        tiktok_url: normalizeDraftTextValue(draft.tiktokUrl),
        hard_skills: Array.isArray(draft.hardSkills) ? draft.hardSkills : [],
        recruitment_tools: Array.isArray(draft.hardSkills) ? draft.hardSkills : [],
        technology_stack: Array.isArray(draft.hardSkills) ? draft.hardSkills : [],
        soft_skills: Array.isArray(draft.softSkills) ? draft.softSkills : [],
        job_boards: Array.isArray(draft.softSkills) ? draft.softSkills : [],
        job_description: normalizeDraftTextValue(draft.jobDescription),
        responsibilities: normalizeDraftTextValue(draft.responsibilities),
        qualifications: normalizeDraftTextValue(draft.qualifications),
        requirements: normalizeDraftTextValue(draft.qualifications),
      };

      const response = await authFetch(
        `${API_BASE}/api/leads/${encodeURIComponent(leadId)}/info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || `Failed to save lead details (${response.status})`);
      }

      await refreshLeadDetails();
    },
    [authFetch, refreshLeadDetails, resolvedLeadId]
  );

  const closeStatusFeedback = useCallback(() => {
    setStatusFeedback((prev) => ({ ...prev, open: false }));
  }, []);

  const handleStatusChange = useCallback(
    async (nextStatusValue) => {
      const leadId = resolvedLeadId;
      const normalizedStatus = normalizeLeadStatusLabel(nextStatusValue, {
        fallback: "",
        preserveUnknown: true,
      });
      const previousStatus = normalizeLeadStatusLabel(
        leadLive?.status || lead?.status || status,
        {
          fallback: "New",
          preserveUnknown: true,
        }
      );

      if (!normalizedStatus) {
        setStatus(previousStatus);
        return;
      }

      if (!leadId || statusSaving) {
        setStatus(normalizedStatus);
        return;
      }

      if (normalizedStatus === previousStatus) {
        setStatus(previousStatus);
        return;
      }

      setStatus(normalizedStatus);
      setStatusSaving(true);
      setLeadLive((prev) =>
        prev
          ? {
              ...prev,
              status: normalizedStatus,
            }
          : prev
      );

      try {
        const response = await authFetch(
          `${API_BASE}/api/leads/${encodeURIComponent(leadId)}/status`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status: normalizedStatus }),
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.error || `Failed to update lead status (${response.status})`
          );
        }

        const savedStatus = normalizeLeadStatusLabel(
          payload?.status || payload?.lead?.status || normalizedStatus,
          {
            fallback: normalizedStatus,
            preserveUnknown: true,
          }
        );

        const nextLead = {
          ...(leadLive || lead || {}),
          ...(payload?.lead && typeof payload.lead === "object" ? payload.lead : {}),
          id: leadId,
          status: savedStatus,
        };
        setStatus(savedStatus);
        setLeadLive((previous) => mergeLeadDetailState(previous, nextLead));
        onLeadChange?.(nextLead);

        const refreshedLead = await refreshLeadDetails().catch((error) => {
          console.error("Failed to refresh lead after status update:", error);
          return null;
        });
        if (refreshedLead) {
          onLeadChange?.({ ...refreshedLead, id: leadId, status: savedStatus });
        }

        setStatusFeedback({
          open: true,
          severity: "success",
          message: `Lead stage updated to "${savedStatus}".`,
        });
      } catch (error) {
        console.error("Failed to update lead status from lead details:", error);
        setStatus(previousStatus);
        setLeadLive((prev) =>
          prev
            ? {
                ...prev,
                status: previousStatus,
              }
            : prev
        );
        setStatusFeedback({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update lead stage.",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [
      authFetch,
      lead,
      leadLive,
      onLeadChange,
      refreshLeadDetails,
      resolvedLeadId,
      status,
      statusSaving,
    ]
  );

  const handleMatchCandidates = useCallback(async () => {
    const leadId = Number(resolvedLeadId || 0);
    if (!leadId || matchingCandidates) return;

    setMatchingCandidates(true);

    try {
      const response = await authFetch(`${API_BASE}/api/leads/match-candidates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lead_ids: [leadId],
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to match candidates (${response.status})`);
      }

      const results = Array.isArray(payload?.results) ? payload.results : [];
      const currentResult =
        results.find((item) => Number(item?.lead_id) === leadId) || results[0] || null;
      const matchedCount = Number(currentResult?.matched_count || 0);
      const nextMatchRows = Array.isArray(currentResult?.matches)
        ? currentResult.matches.map((match, index) => ({
            id: Number(match?.candidate_id || 0) || `match-${index}`,
            lead_id: leadId,
            candidate_id: Number(match?.candidate_id || 0) || null,
            stage: match?.stage || "Matched",
            candidate_name: match?.candidate_name || "Candidate",
            candidate_title: match?.candidate_title || "",
            avatar_url: match?.avatar_url || "",
            candidate_status: match?.candidate_status || "",
            candidate_company: match?.candidate_company || "",
            candidate_location: match?.candidate_location || "",
            candidate_stage: match?.candidate_stage || "",
            skills_json: match?.skills_json || "[]",
            extra_skills_count: Number(match?.extra_skills_count || 0),
            linkedin_url: match?.linkedin_url || "",
            email: match?.email || "",
            has_linkedin: Number(match?.has_linkedin || 0),
            has_email: Number(match?.has_email || 0),
            has_resume: Number(match?.has_resume || 0),
            recruiter_name: match?.recruiter_name || "",
            match_score: Number(match?.match_score ?? match?.score ?? 0),
            match_reason: match?.match_reason || match?.reason || "",
            last_sync_label:
              match?.last_sync_label || `AI ${Math.round(Number(match?.match_score ?? match?.score ?? 0))}%`,
            sort_order:
              match?.sort_order == null || match?.sort_order === ""
                ? index + 1
                : Number(match.sort_order),
          }))
        : [];

      setLeadLive((prev) =>
        prev
          ? {
              ...prev,
              matches_count: matchedCount,
              matchesCount: matchedCount,
            }
          : prev
      );
      setPrefetchedMatchRows(nextMatchRows);
      setMatchesRefreshKey((prev) => prev + 1);
      // Keep the workflow in the Matches tab after the agent finishes so the
      // newly saved, live results are immediately visible.
      setTab("matches");

      await refreshLeadDetails().catch((error) => {
        console.error("Failed to refresh lead details after candidate matching:", error);
      });

      if (!matchedCount) {
        window.alert("No candidate matches were generated for this lead.");
        return;
      }

      window.alert(
        `Matched ${matchedCount} candidate${matchedCount === 1 ? "" : "s"} for ${
          currentResult?.company_name || leadLive?.company_name || lead?.company_name || "this lead"
        }.`
      );
    } catch (error) {
      console.error("Failed to match lead candidates from lead details:", error);
      window.alert(error?.message || "Failed to match candidates for this lead.");
    } finally {
      setMatchingCandidates(false);
    }
  }, [authFetch, lead?.company_name, leadLive?.company_name, matchingCandidates, refreshLeadDetails, resolvedLeadId, setTab]);

  const handleMoveMatchedCandidatesToStage = useCallback(
    async (candidateIds = [], nextStage = "") => {
      const leadId = Number(resolvedLeadId || 0);
      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      const stage = String(nextStage || "").trim();
      if (!leadId) throw new Error("Missing lead ID.");
      if (!normalizedCandidateIds.length) throw new Error("Select at least one candidate.");
      if (!stage) throw new Error("Select a stage.");

      // Stage selection is a navigation action as well as a save. Move the
      // user out of Matches immediately; the persisted result below refreshes
      // the cards and tab counter once the request finishes.
      setLeadMatchRows((previous) =>
        previous.map((row) =>
          normalizedCandidateIds.includes(Number(row?.candidate_id || 0))
            ? { ...row, candidate_stage: stage, stage }
            : row
        )
      );
      setTab("stages");

      const response = await authFetch(
        `${API_BASE}/api/leads/${encodeURIComponent(leadId)}/candidates/stage`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ candidate_ids: normalizedCandidateIds, stage }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to move candidates to ${stage}.`);
      }

      const updatedRows = Array.isArray(payload?.rows) ? payload.rows : [];
      if (updatedRows.length) {
        const updatedByCandidateId = new Map(
          updatedRows.map((row) => [Number(row?.candidate_id || 0), row])
        );
        setLeadMatchRows((previous) =>
          [
            ...previous.map((row) => {
              const updated = updatedByCandidateId.get(Number(row?.candidate_id || 0));
              return updated ? { ...row, ...updated } : row;
            }),
            ...updatedRows.filter(
              (updated) =>
                !previous.some(
                  (row) => Number(row?.candidate_id || 0) === Number(updated?.candidate_id || 0)
                )
            ),
          ]
        );
      }
      const stagesCount = Number(payload?.stagesCount ?? payload?.stages_count);
      if (Number.isFinite(stagesCount) && stagesCount >= 0) {
        setLeadLive((previous) =>
          previous
            ? {
                ...previous,
                stages_count: stagesCount,
                stagesCount,
              }
            : previous
        );
      }
      // Confirm the aggregate from the source of truth so a full reload and
      // the current tab show the same Stages count.
      await refreshLeadDetails().catch((error) => {
        console.error("Failed to refresh lead stage count:", error);
      });
      // Refresh the Matches data for the next visit.
      setMatchesRefreshKey((previous) => previous + 1);
      return payload;
    },
    [authFetch, refreshLeadDetails, resolvedLeadId, setTab]
  );

  const counts = useMemo(
    () => ({
      stages: Math.max(0, toFiniteCount(leadLive?.stagesCount ?? leadLive?.stages_count)),
      matches: Math.max(0, toFiniteCount(leadLive?.matchesCount ?? leadLive?.matches_count)),
      activities: Math.max(0, toFiniteCount(leadLive?.activitiesCount ?? leadLive?.activities_count)),
    }),
    [leadLive]
  );

  const handleMatchRowsChange = useCallback(
    (matchRows = []) => {
      const matchesCount = Array.isArray(matchRows) ? matchRows.length : 0;
      setLeadMatchRows(Array.isArray(matchRows) ? matchRows : []);
      setLeadLive((previous) => {
        if (!previous) return previous;
        const currentCount = toFiniteCount(previous.matchesCount ?? previous.matches_count);
        if (currentCount === matchesCount) return previous;

        const nextLead = {
          ...previous,
          matches_count: matchesCount,
          matchesCount,
        };
        onLeadChange?.(nextLead);
        return nextLead;
      });
    },
    [onLeadChange]
  );

  const tabs = useMemo(
    () => [
      { label: "Details", value: "details" },
      { label: "Stages", value: "stages", count: counts.stages },
      { label: "Matches", value: "matches", count: counts.matches },
      { label: "Activities", value: "activities", count: counts.activities },
    ],
    [counts]
  );

  const data = useMemo(() => {
    const activeLead = mergeLeadDetailState(lead, leadLive) || {};
    const linkedJob = linkedJobLive && typeof linkedJobLive === "object" ? linkedJobLive : {};
    const linkedJobOtherLinks = Array.isArray(linkedJob?.other_links)
      ? linkedJob.other_links
          .map((item) => {
            if (typeof item === "string") return item.trim();
            if (item && typeof item === "object") {
              return String(item.url || item.href || item.value || item.link || "").trim();
            }
            return "";
          })
          .filter(Boolean)
      : normalizeStringArray(linkedJob?.other_links);
    const companyWebsite =
      activeLead?.company_website ||
      activeLead?.website ||
      activeLead?.website_url ||
      linkedJob?.company_website ||
      linkedJob?.website ||
      "";
    const careerWebsite =
      activeLead?.company_career_website ||
      activeLead?.career_site_url ||
      activeLead?.career_website ||
      linkedJob?.company_career_website ||
      linkedJob?.career_website ||
      linkedJob?.post_link ||
      linkedJob?.job_url ||
      "";
    const leadLink =
      activeLead?.lead_link ||
      linkedJob?.post_link ||
      linkedJob?.job_url ||
      "";
    const otherLink =
      activeLead?.other_link ||
      linkedJobOtherLinks[0] ||
      "";
    const hardSkills = normalizeStringArray(
      activeLead?.hard_skills ||
      activeLead?.technology_stack ||
      activeLead?.skills ||
      activeLead?.recruitment_tools ||
      linkedJob?.hard_skills ||
      linkedJob?.tech_stack_tokens ||
      linkedJob?.tech_stack ||
      linkedJob?.skills
    );
    const softSkills = normalizeStringArray(
      activeLead?.soft_skills ||
      activeLead?.job_boards ||
      linkedJob?.soft_skills
    );
    const resolvedCareerWebsite = careerWebsite || leadLink;
    const resolvedCompanyDomain = toDomain(
      companyWebsite || resolvedCareerWebsite || otherLink
    );
    const qualifications =
      activeLead?.qualifications ||
      activeLead?.requirements ||
      linkedJob?.qualifications ||
      "";
    const resolvedJobDescription =
      activeLead?.job_description ||
      activeLead?.full_company_description ||
      linkedJob?.job_description ||
      linkedJob?.description ||
      "";
    const resolvedResponsibilities = resolveDistinctResponsibilitiesText(
      activeLead?.responsibilities || linkedJob?.responsibilities,
      resolvedJobDescription
    );
    const leadDocuments = normalizeDocuments(getLeadDocuments(activeLead));
    const linkedJobDocuments = normalizeDocuments(
      Array.isArray(linkedJob?.documents) ? linkedJob.documents : []
    );
    const leadContacts = Array.isArray(activeLead?.contacts) ? activeLead.contacts : [];
    const linkedJobContacts = Array.isArray(linkedJob?.contacts) ? linkedJob.contacts : [];
    const leadLocations = normalizeLocationFilterValues([
      activeLead?.locations,
      activeLead?.locations_json,
      activeLead?.locationsJson,
      activeLead?.location_values,
      activeLead?.location,
    ]);
    const linkedJobLocations = normalizeLocationFilterValues([
      linkedJob?.locations,
      linkedJob?.locations_json,
      linkedJob?.locationsJson,
      linkedJob?.location_values,
      linkedJob?.location,
    ]);
    const resolvedLocations = leadLocations.length ? leadLocations : linkedJobLocations;

    return {
      entityType: "lead",
      leadId: resolvedLeadId || null,
      companyId: activeLead?.company_id || activeLead?.companyId || null,
      linkedJobId: getLeadLinkedJobId(activeLead, linkedJob),
      jobTitle: resolvedLeadTitle,
      companyName:
        activeLead?.company_name ||
        linkedJob?.company_name ||
        linkedJob?.company ||
        activeLead?.name ||
        "Company",
      companyDomain: resolvedCompanyDomain,
      companyLogo:
        activeLead?.company_logo ||
        activeLead?.companyLogo ||
        linkedJob?.company_logo ||
        linkedJob?.company_logo_url ||
        "",
      primaryContact:
        activeLead?.contact_name ||
        activeLead?.job_primary_contact_name ||
        activeLead?.company_primary_contact_name ||
        linkedJob?.primary_contact ||
        linkedJob?.contact_name ||
        linkedJob?.hiring_manager_name ||
        "",
      titleField:
        activeLead?.job_primary_contact_role ||
        activeLead?.company_primary_contact_role ||
        activeLead?.contact_role ||
        activeLead?.contact_title ||
        linkedJob?.contact_title ||
        linkedJob?.hiring_manager_title ||
        linkedJob?.hiring_manager_role ||
        "Primary Contact",
      salaryRange:
        activeLead?.salary_range ||
        activeLead?.compensation ||
        linkedJob?.salary_range ||
        linkedJob?.compensation ||
        "-",
      locations: resolvedLocations,
      locations_json: resolvedLocations,
      locationsJson: resolvedLocations,
      location:
        (resolvedLocations.length ? resolvedLocations.join(" | ") : "") ||
        activeLead?.location ||
        linkedJob?.location ||
        "-",
      employmentType: formatEmploymentType(
        activeLead?.employment_type ||
          activeLead?.employment ||
          linkedJob?.employment_type ||
          linkedJob?.employment ||
          ""
      ),
      companySize:
        activeLead?.company_size ||
        linkedJob?.company_size ||
        linkedJob?.size_range ||
        "-",
      industries:
        activeLead?.industries ||
        activeLead?.industry_sectors_json ||
        linkedJob?.industries ||
        linkedJob?.industry_sectors_json ||
        [],
      industry:
        activeLead?.industry_sector ||
        activeLead?.industry ||
        linkedJob?.company_industry ||
        linkedJob?.industry ||
        linkedJob?.industry_sector ||
        "-",
      companyWebsite: companyWebsite || "-",
      careerWebsite: resolvedCareerWebsite || "-",
      source: activeLead?.source || linkedJob?.source || "",
      hardSkills: hardSkills.length ? hardSkills : ["-"],
      softSkills: softSkills.length ? softSkills : ["-"],
      leadLink: leadLink || "",
      otherLink: otherLink || "",
      linkedinUrl: activeLead?.linkedin_url || linkedJob?.linkedin_url || "",
      facebookUrl: activeLead?.facebook_url || linkedJob?.facebook_url || "",
      twitterUrl: activeLead?.twitter_url || linkedJob?.twitter_url || "",
      instagramUrl: activeLead?.instagram_url || linkedJob?.instagram_url || "",
      youtubeUrl: activeLead?.youtube_url || linkedJob?.youtube_url || "",
      tiktokUrl: activeLead?.tiktok_url || linkedJob?.tiktok_url || "",
      jobDescription: resolvedJobDescription,
      responsibilities: resolvedResponsibilities,
      qualifications,
      documents: leadDocuments.length ? leadDocuments : linkedJobDocuments,
      contacts: leadContacts.length ? leadContacts : linkedJobContacts,
      activities: Array.isArray(activeLead?.activities) ? activeLead.activities : [],
    };
  }, [lead, leadLive, linkedJobLive, resolvedLeadId, resolvedLeadTitle]);
  const aiCompanySeed = useMemo(
    () => ({
      name: String(data?.companyName || "").trim(),
      website: String(data?.companyWebsite || "").trim(),
      career_site: String(data?.careerWebsite || "").trim(),
      company_link: String(
        data?.companyWebsite || data?.careerWebsite || data?.leadLink || data?.otherLink || ""
      ).trim(),
      created_at: resolveEntityAddedDate(leadLive, lead, linkedJobLive),
      updated_at: String(
        leadLive?.updated_at ||
          leadLive?.updatedAt ||
          lead?.updated_at ||
          lead?.updatedAt ||
          linkedJobLive?.updated_at ||
          linkedJobLive?.updatedAt ||
          ""
      ).trim(),
      notes: data?.companyName
        ? `Create or refresh ${data.companyName}, scan the public website for company information, leadership, team members, and current openings.`
        : "",
    }),
    [
      data?.careerWebsite,
      data?.companyName,
      data?.companyWebsite,
      data?.leadLink,
      data?.otherLink,
      lead,
      leadLive,
      linkedJobLive,
    ]
  );

  const stats = useMemo(() => {
    const contactsCount = Array.isArray(data?.contacts) ? data.contacts.length : 0;
    const activitiesCount = Math.max(
      toFiniteCount(leadLive?.activitiesCount ?? leadLive?.activities_count),
      Array.isArray(data?.activities) ? data.activities.length : 0
    );
    const skillsCount = countDistinctStatsValues([
      ...(Array.isArray(data?.hardSkills) ? data.hardSkills : []),
      ...(Array.isArray(data?.softSkills) ? data.softSkills : []),
    ]);

    return [
      { id: "stages", value: String(counts.stages), label: "Stages" },
      { id: "matches", value: String(counts.matches), label: "Matches" },
      { id: "activities", value: String(activitiesCount), label: "Activities" },
      { id: "contacts", value: String(contactsCount), label: "Contacts" },
      { id: "skills", value: String(skillsCount), label: "Skills" },
    ];
  }, [counts.matches, counts.stages, data, leadLive?.activitiesCount, leadLive?.activities_count]);

  const handleStatItemClick = useCallback((item) => {
    const statId = String(item?.id || "");
    if (["stages", "matches", "activities"].includes(statId)) setTab(statId);
    else if (["contacts", "skills"].includes(statId)) setTab("details");
  }, [setTab]);

  const shareDialogData = useMemo(() => {
    const activeLead = mergeLeadDetailState(lead, leadLive) || {};
    const linkedJob = linkedJobLive && typeof linkedJobLive === "object" ? linkedJobLive : {};
    const contactEmails = uniqTextValues(
      [
        activeLead?.email,
        activeLead?.contact_email,
        activeLead?.job_primary_contact_email,
        activeLead?.company_primary_contact_email,
        activeLead?.email_url,
        linkedJob?.email,
        linkedJob?.contact_email,
        linkedJob?.job_primary_contact_email,
        linkedJob?.company_primary_contact_email,
        linkedJob?.last_activity_email,
        ...(Array.isArray(data?.contacts) ? data.contacts : []).flatMap((contact) => [
          contact?.email,
          contact?.contact_email,
        ]),
      ].filter(Boolean)
    );
    const ownerNames = uniqTextValues([
      leadLive?.owner_name,
      leadLive?.owner,
      activeLead?.owner_name,
      activeLead?.owner,
      activeLead?.assigned_team_member,
      activeLead?.team_member,
    ]);

    return {
      resetKey: `${data?.leadId || "lead"}:${data?.linkedJobId || "job"}`,
      summaryItems: uniqTextValues([
        data?.jobTitle,
        data?.companyName,
        data?.primaryContact,
        data?.location,
        data?.employmentType,
      ]),
      defaultTeamMembers: ownerNames.slice(0, 1),
      defaultEmailRecipients: contactEmails.slice(0, 1),
      emailSuggestions: contactEmails,
      notesDefault: String(
        activeLead?.full_company_description ||
          activeLead?.notes ||
          activeLead?.description ||
          data?.jobDescription ||
          linkedJob?.description ||
          linkedJob?.job_description ||
          ""
      ).trim(),
    };
  }, [data, lead, leadLive, linkedJobLive]);

  const handleCreatePitchPackage = useCallback(() => {
    setPitchPackageDialogOpen(true);
  }, []);

  const sequenceItems = useMemo(() => {
    if (!canPersistLead) return [];
    const activeLead = mergeLeadDetailState(lead, leadLive) || {};
    return [buildLeadSequenceItem({ ...activeLead, id: resolvedLeadId, lead_id: resolvedLeadId }, "leads")];
  }, [canPersistLead, lead, leadLive, resolvedLeadId]);

  const runLoggedQuickAction = useCallback(
    (_actionKey, _actionLabel, actionFn) => () => {
      actionFn?.();
    },
    []
  );

  const quickActionItems = useMemo(
    () => [
      {
        key: "share",
        label: "Share",
        onClick: runLoggedQuickAction("share", "Share", () => setShareDialogOpen(true)),
        icon: <IosShareOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "create-sequence",
        label: "Create Sequence",
        onClick: runLoggedQuickAction("create_sequence", "Create Sequence", () => setSequenceDialogOpen(true)),
        disabled: !canPersistLead,
        icon: <SequenceActionIcon size={16} />,
      },
      {
        key: "pitch-package",
        label: "Pitch Package",
        onClick: runLoggedQuickAction(
          "pitch_package",
          "Pitch Package",
          handleCreatePitchPackage
        ),
        icon: <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "assign-team",
        label: "Assign Team",
        onClick: runLoggedQuickAction("assign_team", "Assign Team", () => setAssignTeamDialogOpen(true)),
        icon: <AssignTeamPillIcon />,
      },
      {
        key: "match-candidates",
        label: matchingCandidates ? "Matching..." : "Match Candidates",
        onClick: runLoggedQuickAction("match_candidates", "Match Candidates", handleMatchCandidates),
        icon: <MatchActionIcon size={16} />,
        disabled: matchingCandidates || !canPersistLead,
      },
    ],
    [
      canPersistLead,
      handleCreatePitchPackage,
      handleMatchCandidates,
      matchingCandidates,
      runLoggedQuickAction,
    ]
  );

  return (
    <>
      <ListTableTabsTemplate
        rootSx={{ py: 2 }}
        shellSx={{ pb: 0 }}
        statsSection={
          <StatsSlider
            items={stats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={["stages", "matches", "activities"].includes(tab) ? tab : null}
          />
        }
        headerSection={
          <>
            <ProfileHeader
              title={data.jobTitle}
              tabs={tabs}
              tab={tab}
              onTabChange={(value) => setTab(normalizeTab(value))}
              actionValue={status}
              onActionChange={handleStatusChange}
              actionLabel="Status"
              actionPlaceholder="Lead Status"
              actionOptions={LEAD_STATUS_ACTION_OPTIONS}
              actionShowPlaceholderOption={false}
              actionDisabled={statusSaving || !canPersistLead}
              actionFeedback={statusFeedback}
              onActionFeedbackClose={closeStatusFeedback}
              metaLabel="Team Member"
              metaValue={leadLive?.owner_name || leadLive?.owner || lead?.owner_name || lead?.owner || "-"}
              sx={TAB_VIEW_HEADER_SX}
            />
            {canPersistLead && <DiscoveryScheduling leadId={resolvedLeadId} status={status} contactName={data.primaryContact} onSaved={refreshLeadDetails} actionTarget={discoveryActionTarget} showMeetings={false} />}
            {activeLeadEnrichment ? (
              <Alert
                severity="info"
                icon={false}
                sx={{ mx: 2.5, mb: 1.5, borderRadius: 2, alignItems: "center" }}
              >
                <Box sx={{ minWidth: 260 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                    AI Agent is enriching this lead and company profile
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This continues in the background. The page will refresh when it is complete.
                  </Typography>
                  <LinearProgress
                    variant={leadEnrichmentProgress > 0 ? "determinate" : "indeterminate"}
                    value={leadEnrichmentProgress}
                    sx={{ mt: 0.75, borderRadius: 2 }}
                  />
                </Box>
              </Alert>
            ) : null}
          </>
        }
        contentSection={
          <JobDetailsContent
            tab={tab}
            data={{ ...data, scheduleActionTargetRef: setDiscoveryActionTarget }}
            fieldOptions={detailFieldOptions}
            stageRows={leadMatchRows}
            onMoveCandidatesToStage={
              canPersistLead ? handleMoveMatchedCandidatesToStage : undefined
            }
            onSaveDetails={canPersistLead ? handleSaveDetails : undefined}
            onAddDocument={canPersistLead ? handleAddDocument : undefined}
            documentsUploading={documentsUploading}
            documentsError={documentsError}
            prefetchedMatchRows={prefetchedMatchRows}
            matchesRefreshKey={matchesRefreshKey}
            onMatchRowsChange={handleMatchRowsChange}
            onMoveMatchCandidatesToStage={
              canPersistLead ? handleMoveMatchedCandidatesToStage : undefined
            }
            showDocuments={false}
          />
        }
        contentWrapperSx={TAB_VIEW_CONTENT_WRAPPER_SX}
        rightSidebar={
          <>
            <QuickActionsCard
              actionItems={quickActionItems}
              titleSx={{
                mb: 1.75,
              }}
              actionsContainerSx={{
                gap: 1.5,
              }}
              buttonSx={{
                height: 36,
                px: 2,
                fontSize: 12.5,
                fontWeight: 500,
                letterSpacing: 0,
                boxShadow: "none",
                "& .MuiButton-startIcon": {
                  mr: 0.75,
                  ml: 0,
                },
                "& .MuiSvgIcon-root": {
                  fontSize: 16,
                },
              }}
            />
            <AIAgentCard companySeed={aiCompanySeed} entityType="lead" entityId={resolvedLeadId} entityName={resolvedLeadTitle} />
            <HiringTeamCard
              apiBase={API_BASE}
              entityType="lead"
              entityId={data.leadId}
              source={leadLive?.source || ""}
            />
          </>
        }
        rightWidth={392}
        rightSidebarSx={{ flexShrink: 0 }}
        leftColumnSx={{
          borderRadius: 3,
          border: `1px solid ${BORDER}`,
        }}
      />

      <AddToSequenceDialog
        open={sequenceDialogOpen}
        onClose={() => setSequenceDialogOpen(false)}
        entityType="lead"
        items={sequenceItems}
        user={user}
        onCreated={() => {
          setSequenceDialogOpen(false);
          navigate("/content?tab=sequences");
        }}
      />

      <ShareJobDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        apiBase={API_BASE}
        resetKey={`${shareDialogData.resetKey}:share`}
        summaryItems={shareDialogData.summaryItems}
        defaultTeamMembers={shareDialogData.defaultTeamMembers}
        defaultEmailRecipients={shareDialogData.defaultEmailRecipients}
        emailSuggestions={shareDialogData.emailSuggestions}
        notesDefault={shareDialogData.notesDefault}
      />

      <ShareJobDialog
        open={assignTeamDialogOpen}
        onClose={() => setAssignTeamDialogOpen(false)}
        apiBase={API_BASE}
        resetKey={`${shareDialogData.resetKey}:assign`}
        title="Assign Team Member"
        sectionTitle="Assign"
        submitLabel="Assign Team Member"
        showEmailField={false}
        documentLayout="inline"
        headerIcon={<TeamMemberAddIcon size={20} color="#3451F4" />}
        summaryItems={shareDialogData.summaryItems}
        defaultTeamMembers={shareDialogData.defaultTeamMembers}
        notesDefault={shareDialogData.notesDefault}
        defaultDocuments={data.documents}
      />

      <PitchPackageDialog
        open={pitchPackageDialogOpen}
        onClose={() => {
          setPitchPackageDialogOpen(false);
        }}
        context={{
          companyName: data.companyName,
          jobTitle: data.jobTitle,
          location: data.location,
          industry: data.industry,
          primaryContact: data.primaryContact,
        }}
      />
    </>
  );
}
